import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import { Typography, Box, Accordion, AccordionDetails, IconButton } from '@mui/material/';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import classes from './ArticleCard.module.scss';
import { ArticleCardProps } from '../../interfaces/props/ArticleCardProps';
import Tags from '../../views/tags/Tags';
import { Tag } from '../../interfaces/Tag';
import { setTags } from '../../../store/filters/filters.slice';
import DateHelper from '../../helpers/date.helper';
import ImageWrapper from '../image-wrapper/ImageWrapper';
import TypeChip from '../../views/type-chip/TypeChip';
import ArticleCardDetails from '../../views/article-card-details/ArticleCardDetails';

const fallbackImage = <Box
  component={'img'}
  className='image-placeholder-all-articles'
/>;

const ArticleCard = ({
  id,
  title,
  summary,
  image,
  createdAt,
  siteName,
  source,
  tags,
  facts,
  tipsAndPredictions,
  tvChannels,
  type,
  numberOfWords
}: ArticleCardProps) => {
  const [isExpended, setIsExpended] = useState<boolean>(false);
  const dispatch = useDispatch();

  const tagClickHandler = (tag: Tag) => {
    dispatch(setTags([tag]));
  };

  const renderImage = useMemo(() => {
    if (image.length > 0) {
      return <ImageWrapper
        className='image-all-articles'
        alt='article'
        src={image}
        fallbackElement={fallbackImage}
      />
    }

    return fallbackImage;
  }, [image]);

  const calculateTimeDifference: string = useMemo(() => {
    return DateHelper.calculateTimeDifferenceFromNow(dayjs(createdAt));
  }, [createdAt]);

  return (
    <Accordion expanded={isExpended}>
      <Box className={`${classes.card} d-flex border w-100`}>
        <Box className={classes.imageBox}>
          <Box className={`${classes.overlay} w-100`}></Box>
          <TypeChip type={type} />
          <Link
            to={`../article/${id}`}
            style={{ textDecoration: 'none', color: 'black' }}
          >
            {renderImage}
          </Link>
        </Box>
        <Box className='d-flex flex-column justify-content-between w-100'>
          <Box>
            <Link
              to={`../article/${id}`}
              style={{ textDecoration: 'none', color: 'black' }}
            >
              <Box className={`${classes['time-ago']} d-flex justify-content-between pt-3`}>
                <Typography
                  className='px-3 text-headline'
                >
                  {title}
                </Typography>
                <Typography
                  align='right'
                  className={`${classes['date-color']} pt-2 px-3 fs-7`}
                >
                  {calculateTimeDifference}
                </Typography>
              </Box>
              <Typography className={`${classes['summary']} px-3 pt-3 text-base`}>
                {summary}
              </Typography>
            </Link>
            <Box className='ps-2'>
              <Tags tags={tags} onTagClick={tagClickHandler} />
            </Box>
          </Box>
          <Box className='d-flex mb-2'>
            <Typography
              className={`${classes['original-on-text']} py-3 px-3 fs-7 d-flex`}
            >
              Original article on:
              <Link
                className={`${classes['link']} d-flex fs-7 px-2`}
                to={source}
                target='_blank'
                rel='noopener noreferrer'
              >
                {siteName}
              </Link>
            </Typography>
            { numberOfWords > 0 ? 
            <Typography
              className={`${classes['original-on-text']} py-3 ps-3 fs-7 d-flex`}
            >
              Word count: {numberOfWords}
            </Typography> : <></>
            }
            <Link
              to={`../article/${id}`}
              style={{ textDecoration: 'none', color: 'black', flex: '1' }}
            ></Link>
          </Box>
        </Box>
        <IconButton
          className={`${isExpended ? classes.extendedButton : classes.extendButton} border`}
          onClick={() => setIsExpended(!isExpended)}
          size='small'
        >
          <ExpandMoreIcon className={`${isExpended ? classes.extendedButtonIcon : classes.extendButtonIcon}`} />
        </IconButton>
      </Box>
      <AccordionDetails className={classes.details}>
        <ArticleCardDetails facts={facts} tipsAndPredictions={tipsAndPredictions} tvChannels={tvChannels} />
      </AccordionDetails>
    </Accordion>
  );
};

export default ArticleCard;
